import { ThemeColorModes, setGlobalTheme } from "@atlaskit/tokens";
import React, { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { getLocalStorageItem, LocalStorageKey, setLocalStorageItem } from "../clients/localStorage/localStorageClient";
import { logInfo } from "../utils/logger";
import { postMessageToParent } from "../message-bus/postMessageToParent";

type ThemeContextType = {
  localThemeColorMode: ThemeColorModes;
  setLocalThemeColorMode: (newTheme: ThemeColorModes) => void;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

const useThemeLogic = (): ThemeContextType => {
  const [themeColorMode, setThemeColorMode] = useState<ThemeColorModes>(
    getLocalStorageItem(LocalStorageKey.THEME_COLOR_MODE, "auto") ?? "auto",
  );

  const setLocalThemeColorMode = (newTheme: ThemeColorModes): void => {
    setThemeColorMode(newTheme);
    setLocalStorageItem(LocalStorageKey.THEME_COLOR_MODE, newTheme);

    // Always post message to parent for theme changes (visual refresh enabled)
    postMessageToParent({ type: "SET_SIDEPANEL_THEME", theme: newTheme });
  };

  useEffect(() => {
    // for custom background color
    document.body.classList.remove("theme-light", "theme-dark");
    document.documentElement.classList.remove("theme-light", "theme-dark");

    if (themeColorMode === "light" || themeColorMode === "dark") {
      document.body.classList.add(`theme-${themeColorMode}`);
      document.documentElement.classList.add(`theme-${themeColorMode}`);
    }

    // for sub-components theme
    setGlobalTheme({
      light: "light",
      dark: "dark",
      colorMode: themeColorMode,
      typography: "typography",
    }).catch(() => {
      logInfo("Failed to set global theme.");
    });
  }, [themeColorMode]);

  return { localThemeColorMode: themeColorMode, setLocalThemeColorMode };
};

export const ThemeProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const theme = useThemeLogic();
  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};

export const useThemeContext = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
